import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface UserFilters {
  limit?: number;
  offset?: number;
  search?: string;
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
  is_active?: boolean;
  is_deleted?: boolean;
  created_at?: string;
  include_inactive?: string | boolean;
}

export interface UserApiResponse {
  users: any[];
  total: number;
  limit: number;
  offset: number;
  total_pages: number;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly API_BASE_URL = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get all users with optional filters
   * Endpoint: GET /api/v1/users
   */
  getAllUsers(filters?: UserFilters): Observable<UserApiResponse> {
    let params = new HttpParams();

    if (filters) {
      if (filters.limit) {
        params = params.set('limit', filters.limit.toString());
      }
      if (filters.offset) {
        params = params.set('offset', filters.offset.toString());
      }
      if (filters.search) {
        params = params.set('search', filters.search);
      }
      if (filters.name) {
        params = params.set('name', filters.name);
      }
      if (filters.email) {
        params = params.set('email', filters.email);
      }
      if (filters.phone) {
        params = params.set('phone', filters.phone);
      }
      if (filters.role) {
        params = params.set('role', filters.role);
      }
      if (filters.is_active !== undefined) {
        params = params.set('is_active', filters.is_active.toString());
      }
      if (filters.is_deleted !== undefined) {
        params = params.set('is_deleted', filters.is_deleted.toString());
      }
      if (filters.created_at) {
        params = params.set('created_at', filters.created_at);
      }
      if (filters.include_inactive !== undefined) {
        params = params.set('include_inactive', filters.include_inactive.toString());
      }
    }

    return this.http.get<UserApiResponse>(`${this.API_BASE_URL}/user/list`, {
      params,
      withCredentials: true
    });
  }

  /**
   * Get single user by ID
   * Endpoint: GET /api/v1/users/{userId}
   */
  getUserById(userId: string | number): Observable<any> {
    return this.http.get<any>(`${this.API_BASE_URL}/user/${userId}`, {
      withCredentials: true
    });
  }

  /**
   * Create a new user
   * Endpoint: POST /api/v1/users
   */
  createUser(userData: any): Observable<any> {
    return this.http.post<any>(`${this.API_BASE_URL}/user/create`, userData, {
      withCredentials: true
    });
  }

  /**
   * Update an existing user
   * Endpoint: PUT /api/v1/users/update
   * Note: ID should be included in the userData body
   */
  updateUser(userData: any): Observable<any> {
    return this.http.put<any>(`${this.API_BASE_URL}/user/update`, userData, {
      withCredentials: true
    });
  }

  /**
   * Delete a user
   * Endpoint: DELETE /api/v1/users/{userId}
   */
  deleteUser(userId: string | number): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/api/v1/users/${userId}`, {
      withCredentials: true
    });
  }
}

